<!--
  employeeCardAvailabilities: availabilities row of the employee card dialog (AvailabilityPanel: label filter, Terminabgleich,
  mini week timeline, "Zuletzt aktualisiert … von") with the same real-time loading, edit dialog and guards as the former
  employeeAvailabilitiesListItem.
  Props: employee
-->
<template>
  <div>
    <div v-if="employee.availabilities || (canReadAvailabilities && canUpdateAvailabilities)" class="fld">
      <span class="fld__icon"><v-icon>mdi-clock-outline</v-icon></span>
      <div style="min-width: 0">
        <span class="fld__label">Verfügbarkeiten</span>
        <div v-if="!employee.availabilities" class="fld__value fld__value--muted">Für diese Lehrkraft wurden keine Verfügbarkeiten hinterlegt</div>
        <availability-panel v-else :loading="loading" :real-time="disableRealTimeAvailabilities ? undefined : realTimeAvailabilities"
                            :stored="employee.availabilities"/>
      </div>
      <div class="fld__actions">
        <button v-if="employee.availabilities" v-can:update-employees="{ id: employee._id, keys: ['availabilities'] }"
                aria-label="Verfügbarkeiten bearbeiten" class="ibtn" type="button" v-tip="'Verfügbarkeiten bearbeiten'"
                @click="showUpdateAvailabilitiesDialog = true">
          <v-icon>mdi-pencil-outline</v-icon>
        </button>
        <button v-else aria-label="Verfügbarkeiten hinterlegen" class="ibtn ibtn--add" type="button" v-tip="'Verfügbarkeiten hinterlegen'"
                @click="showUpdateAvailabilitiesDialog = true">
          <v-icon>mdi-plus</v-icon>
        </button>
      </div>
    </div>
    <update-employee-availabilities-dialog v-model="showUpdateAvailabilitiesDialog" :employee-id="employee._id"
                                           @update:availabilities="handleAvailabilityUpdate"/>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {Employee} from '@/interfaces/employee.interface';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import UpdateEmployeeAvailabilitiesDialog
  from '@/components/employees/employeeView/profileView/availabilitiesListItem/updateEmployeeAvailabilitiesDialog.vue';
import {AvailabilityInterface, LabeledAvailabilityInterface} from '@/interfaces/availability.interface';
import {SchedulerApi} from '@/classes/api/scheduler.api.class';
import AvailabilityPanel from '@/components/common/availability/AvailabilityPanel.vue';

export default Vue.extend({
  components: {AvailabilityPanel, UpdateEmployeeAvailabilitiesDialog},
  props: {
    employee: {
      type: Object as () => Employee,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    showUpdateAvailabilitiesDialog: false,
    disableRealTimeAvailabilities: false,
    realTimeAvailabilities: [] as LabeledAvailabilityInterface[],
  }),
  computed: {
    canReadAvailabilities(): boolean {
      return this.$$getCrudEntity(CrudEntityTypes.EMPLOYEE, this.employee._id).canRead(EmployeeFields.AVAILABILITIES);
    },
    canUpdateAvailabilities(): boolean {
      return this.$$getCrudEntity(CrudEntityTypes.EMPLOYEE, this.employee._id).canUpdate(EmployeeFields.AVAILABILITIES);
    },
  },
  methods: {
    handleAvailabilityUpdate(availabilities: AvailabilityInterface[] | null) {
      this.$set(this.employee, 'availabilities', availabilities);
      this.initialize();
    },
    async initialize() {
      const entity = this.$$getCrudEntity(CrudEntityTypes.EMPLOYEE, this.employee._id);
      if (!entity.canRead(EmployeeFields.AVAILABILITIES)) {
        this.disableRealTimeAvailabilities = true;
        return;
      }
      this.loading = true;
      try {
        const resp = await SchedulerApi.getAvailabilities({
          type: CrudEntityTypes.EMPLOYEE,
          id: this.employee._id,
        });
        this.realTimeAvailabilities = resp.availabilities;
      } catch (e) {
        this.$$showSnackbar('Fehler beim Laden der Echtzeitverfügbarkeiten - verwende statische Werte', 'error', e);
        this.disableRealTimeAvailabilities = true;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.initialize();
    if (this.$route.query.instantUpdateAvailabilities === 'true') {
      this.showUpdateAvailabilitiesDialog = true;
    }
  },
  watch: {
    showUpdateAvailabilitiesDialog(v) {
      if (!v && this.$route.query.instantUpdateAvailabilities === 'true') {
        this.$router.push({
          query: {
            instantUpdateAvailabilities: 'false',
          },
        }).catch();
      }
    },
  },
});
</script>

<style src="@/components/acquisitionList/ui/itemDetails.css"></style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
